import { Box, Button, Checkbox, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from "@mui/material";
import { formatarValorMonetario } from "../../../../../../helpers/masks";
import { TFormState } from ".";
import { useMemo, useState } from "react";
import { useMutation } from "react-query";
import { enqueueSnackbar } from "notistack";
import axiosClient from "../../../../../../lib/axios";
import LabelPrinter from "../../../../../../components/LabelPrinter";

export default function Step02({
    form,
    handleBack,
    setOpenCancelRecibo,
    handleChangeProcesso
}: Step02Props) {

    const [selected, setSelected] = useState<readonly number[]>([])
    const [observacoes, setObservacoes] = useState<string>("");



    //querys e mutations
    const { mutate: SolicitarCancelamentoRecibo, isLoading } = useMutation({
        mutationKey: 'financeiro/contas-receber/solicitar-cancelamento-recibo',
        mutationFn: (input: { numero_recibo: number, titulos: any[], observacoes: string }) => axiosClient.post(`financeiro/contas-receber/solicitar-cancelamento-recibo`, input),
        onError: (error) => {
            enqueueSnackbar(`Erro ao solicitar o cancelamento do recibo`, { variant: "error" });
        },
        onSuccess: (data) => {
            enqueueSnackbar(`Cancelamento solicitado com sucesso!`, { variant: "success" });
            handleChangeProcesso(data.data.processo)
            setOpenCancelRecibo(false)
        }
    });

    //funçoes manipuladoras
    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            const newSelected = form.titulosRelacionados.map((n, index) => index);
            setSelected(newSelected);
            return;
        }
        setSelected([]);
    };

    const handleClickRow = (_: React.MouseEvent<unknown>, id: number) => {
        const selectedIndex = selected.indexOf(id);
        let newSelected: readonly number[] = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1),
            );
        }
        setSelected(newSelected);
    };

    const handleSubmit = () => {
        const titulosSelecionados = selected.map(index => form.titulosRelacionados[index]);

        SolicitarCancelamentoRecibo({
            numero_recibo: form.numRecibo,
            titulos: titulosSelecionados,
            observacoes,
        });
    };

    const { total, qtdTitulos } = useMemo(() => {
        const titulosSelecionados = selected.map(index => form.titulosRelacionados[index]);

        const total = titulosSelecionados.reduce((total, item) => total + item.valor_movimento, 0)
        const qtdTitulos = String(titulosSelecionados.length)

        return { total, qtdTitulos }
    }, [selected, form.titulosRelacionados])

    //valores importantes
    const numSelected = selected.length
    const rowCount = form.titulosRelacionados.length
    const bloquearAvanco = selected.length === 0 || isLoading
    //

    return (
        <>
            <TableContainer sx={{ maxHeight: '40vh' }}>
                <Table aria-label="simple table" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox">
                                <Checkbox
                                    indeterminate={numSelected > 0 && numSelected < rowCount}
                                    checked={rowCount > 0 && numSelected === rowCount}
                                    color="primary"
                                    onChange={handleSelectAllClick}
                                    slotProps={{ input: { 'aria-label': 'select all títulos vinculados' } }}
                                />
                            </TableCell>

                            <TableCell><b>Título</b></TableCell>

                            <TableCell align="right"><b>Valor do movimento</b></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {form.titulosRelacionados.map((row, index) => {
                            const isItemSelected = selected.includes(index);
                            const labelId = `enhanced-table-checkbox-${row.titulo}`;

                            return (
                                <TableRow
                                    key={row.titulo}
                                    onClick={(event) => handleClickRow(event, index)}
                                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                >
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            color="primary"
                                            checked={isItemSelected}
                                            slotProps={{
                                                input: { 'aria-labelledby': labelId }
                                            }}
                                        />
                                    </TableCell>

                                    <TableCell component="th" scope="row">
                                        {row.titulo}
                                    </TableCell>

                                    <TableCell align="right">{formatarValorMonetario(row.valor_movimento)}</TableCell>
                                </TableRow>
                            )
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            {numSelected > 0 && (
                <Box sx={{ mt: 3 }}>
                    <TextField
                        label="Motivo(s) do cancelamento:"
                        fullWidth
                        variant="outlined"
                        value={observacoes}
                        onChange={(e) => setObservacoes(e.target.value)}
                        multiline
                        minRows={4}
                    />
                </Box>
            )}
            {
                isLoading &&
                <Box display="flex" justifyContent="center" my={4}>
                    <CircularProgress />
                </Box>
            }
            {
                !isLoading &&
                <Box sx={{ mt: 3 }}>
                    <Box display="flex" justifyContent="space-between">
                        <Box display="flex" gap={6} flexGrow={1} justifyContent="flex-start">
                            {selected.length > 0 && (
                                <>
                                    <LabelPrinter size='small' titulo='Total' valor={formatarValorMonetario(total)} />
                                    <LabelPrinter size='small' titulo='Qtd. titulos' valor={qtdTitulos} />
                                </>
                            )}
                        </Box>
                        <Box display="flex" gap={2}>
                            <Button
                                variant="text"
                                onClick={handleBack}
                                sx={{ mt: 1, mr: 1 }}
                            >
                                Voltar
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleSubmit}
                                sx={{ mt: 1, mr: 1 }}
                                disabled={bloquearAvanco || isLoading}
                            >
                                {isLoading ? 'Solicitando...' : 'Solicitar'}
                            </Button>
                        </Box>
                    </Box>

                </Box>
            }
        </>
    )
}

interface Step02Props {
    form: TFormState
    handleChangeProcesso: (item: string) => void
    handleBack: () => void
    setOpenCancelRecibo: (open: boolean) => void
}